import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { localAgentAgents, localAgentStatus } from "../api/localAgent";
import type { AgentInfo, LocalAgentStatus } from "../api/types";

const UNAVAILABLE: LocalAgentStatus = { available: false, stm32: false, godex: false };
const LS_KEY = "localAgent.selectedId";

export interface LocalAgentValue extends LocalAgentStatus {
  status: LocalAgentStatus;
  agents: AgentInfo[];
  selectedId: string | null;
  select: (id: string | null) => void;
}

const Ctx = createContext<LocalAgentValue | null>(null);

export function LocalAgentProvider({ children }: { children: ReactNode }) {
  const [agents, setAgents] = useState<AgentInfo[]>([]);
  const [status, setStatus] = useState<LocalAgentStatus>(UNAVAILABLE);
  const [selectedId, setSelectedId] = useState<string | null>(
    () => window.localStorage.getItem(LS_KEY),
  );

  // Selection lives in a ref too so `refresh` can stay stable (no re-created
  // interval on every selection change — that caused a polling storm) while
  // still reading the latest choice.
  const selectedIdRef = useRef<string | null>(selectedId);

  const applySelection = useCallback((id: string | null) => {
    selectedIdRef.current = id;
    setSelectedId(id);
    if (id) window.localStorage.setItem(LS_KEY, id);
    else window.localStorage.removeItem(LS_KEY);
  }, []);

  const select = useCallback((id: string | null) => applySelection(id), [applySelection]);

  const refresh = useCallback(async () => {
    const list = await localAgentAgents();
    setAgents(list);
    let target = selectedIdRef.current;
    // Auto-select ONLY when nothing is chosen yet. Do NOT clear an existing
    // selection just because the list momentarily came back empty (a reconnect
    // blip) — clearing it stopped the status poll, which is the only keepalive,
    // so the agent went silent and the backend dropped it (churn loop).
    if (!target && list.length === 1) {
      target = list[0].agent_id;
      applySelection(target);
    }
    // Always poll the selected agent's status — even if the list blipped empty.
    // The status round-trip doubles as the connection's keepalive.
    setStatus(target ? await localAgentStatus(target) : UNAVAILABLE);
  }, [applySelection]);

  useEffect(() => {
    void refresh();
    const onFocus = () => void refresh();
    window.addEventListener("focus", onFocus);
    const interval = setInterval(() => void refresh(), 20_000);
    return () => {
      window.removeEventListener("focus", onFocus);
      clearInterval(interval);
    };
  }, [refresh]);

  const value = useMemo<LocalAgentValue>(
    () => ({ ...status, status, agents, selectedId, select }),
    [status, agents, selectedId, select],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useLocalAgent(): LocalAgentValue {
  const v = useContext(Ctx);
  // Fallback keeps components usable if rendered outside the provider.
  if (!v) return { ...UNAVAILABLE, status: UNAVAILABLE, agents: [], selectedId: null, select: () => {} };
  return v;
}
